import { ssoAPIHelper } from 'services/SSOAPIHelper';

import { getTranslationsFromApi } from 'utils';
import ServerConfig from 'utils/ServerConfig';

const parseLevelRules = (levelRules) =>
  levelRules.map((rule) => {
    const translations = getTranslationsFromApi(rule, ['title', 'content']);

    return {
      id: rule.id,
      title: rule.title,
      content: rule.content,
      displayOrder: rule.display_order,
      translations,
    };
  });

const parseLevelRetainTips = (tips) => {
  const translations = getTranslationsFromApi(tips, ['title', 'description']);

  return {
    title: tips.title,
    description: tips.description,
    translations,
  };
};

const parseSingleLevelBenefits = (benefits) =>
  benefits?.map((benefit) => {
    const translations = getTranslationsFromApi(benefit, ['title', 'content']);

    return {
      id: benefit.id,
      title: benefit.title,
      content: benefit.content,
      displayOrder: benefit.display_order,
      highlight: benefit.highlight,
      translations,
    };
  });

const parseLevelBenefits = (levels) =>
  levels?.map((level) => {
    const translations = getTranslationsFromApi(level, ['level_name']);

    return {
      levelId: level.id,
      levelName: level.level_name,
      levelBenefits: parseSingleLevelBenefits(level.level_benefits),
      translations,
    };
  });

const parseLevelData = (data) => {
  if (!data) {
    return {};
  }

  const translations = getTranslationsFromApi(data, ['level_name']);

  return {
    id: data.id,
    nextLevelId: data.next_level_id,
    levelName: data.level_name,
    cardForegroundColor: data.foreground_element_color,
    cardBackgroundType: data.membership_card_background_type,
    cardBackgroundColor: data.membership_card_background_color,
    membershipCardbackgroundImage: data.card_landscape_background_image_in_web,
    myCardbackgroundImage: data.card_portrait_background_image_in_web,
    hideLevelDetails: data.hide_level_details_and_upgrade_tips,
    // entry requirement
    isLowestLevel: data.is_lowest_level,
    levelRank: data.level_rank,
    enableCheckTargetTotalPointsEarned:
      data.enable_check_target_total_points_earned,
    targetTotalPointsEarned: data.target_total_points_earned,

    enableCheckAccumulatedSpendingInAPeriod:
      data.enable_check_accumulated_spending_in_a_period,
    accumulatedSpendingInThePeriod: data.accumulated_spending_in_the_period,
    accumulatedSpendingInXDay: data.accumulated_spending_in_x_day,

    enableCheckSpendingInOnePurchase:
      data.enable_check_spending_in_one_purchase,
    spendingInOnePurchase: data.spending_in_one_purchase,

    // renewal setting
    renewLogic: data.renew_logic,
    enableCheckRequiredValue:
      data.enable_check_calculation_source_value_within_period_for_retain,
    retainRequiredValue: data.retain_required_value,

    enableCheckEffectiveTransactionTimes:
      data.enable_check_effective_transaction_times_within_period,
    requiredTransactionTimes: data.required_transaction_times,
    minimumAmountOfOneTransactionForRenew:
      data.minimum_amount_of_one_transaction_for_renew,

    // downgrade setting
    downgradeLogic: data.downgrade_logic,
    minimumDowngradeLevel: data.minimum_downgrade_level,
    renewForceDowngradeLevel: data.renew_force_downgrade_level,
    translations,
  };
};

const parseLevelConfig = (data) => data?.map((item) => parseLevelData(item));

export default {
  namespace: 'levels',
  state: {
    levelRules: [],
    levelRulesLoading: false,
    levelRetainTips: {},
    levelRetainTipsLoading: false,
    levelBenefits: [],
    levelBenefitsLoading: false,
    levelConfig: [],
  },

  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
  },
  effects: {
    fetchLevelRules: [
      function* (action, { call, put }) {
        yield put({
          type: 'updateState',
          payload: { levelRulesLoading: true },
        });

        try {
          const response = yield call(ssoAPIHelper.getLevelRules);
          if (response?.status < 300) {
            const levelRules = parseLevelRules(response.data);
            yield put({
              type: 'updateState',
              payload: { levelRules },
            });
          }
        } catch (error) {
          console.log('@@fetchLevelRules error', error);
        }

        yield put({
          type: 'updateState',
          payload: { levelRulesLoading: false },
        });
      },
      { type: 'takeLatest' },
    ],

    fetchLevelRetainTips: [
      function* (action, { call, put }) {
        yield put({
          type: 'updateState',
          payload: { levelRetainTipsLoading: true },
        });

        try {
          const response = yield call(ssoAPIHelper.getLevelRetainTips);
          if (response?.status < 300) {
            const levelRetainTips = parseLevelRetainTips(response.data);
            yield put({
              type: 'updateState',
              payload: { levelRetainTips },
            });
          }
        } catch (error) {
          console.log('@@fetchLevelRetainTips error', error);
        }

        yield put({
          type: 'updateState',
          payload: { levelRetainTipsLoading: false },
        });
      },
      { type: 'takeLatest' },
    ],

    fetchLevelBenefits: [
      function* (action, { call, put }) {
        yield put({
          type: 'updateState',
          payload: { levelBenefitsLoading: true },
        });

        try {
          const response = yield call(ssoAPIHelper.getLevelBenefits);
          if (response?.status < 300) {
            const levelBenefits = parseLevelBenefits(response.data);
            yield put({
              type: 'updateState',
              payload: { levelBenefits },
            });
          }
        } catch (error) {
          console.log('@@fetchLevelBenefits error', error);
        }

        yield put({
          type: 'updateState',
          payload: { levelBenefitsLoading: false },
        });
      },
      { type: 'takeLatest' },
    ],

    fetchLevelConfig: [
      function* (action, { call, put, select }) {
        if (ServerConfig.getNoLevel()) {
          return;
        }
        try {
          const response = yield call(ssoAPIHelper.getLevelConfig);
          if (response?.status === 200) {
            const levelConfig = parseLevelConfig(response.data);
            yield put({
              type: 'updateState',
              payload: {
                levelConfig,
              },
            });
          }
        } catch (error) {
          console.log('@@fetchLevelConfig error', error);
        }
      },
    ],
  },
};
